import { useEffect, useState, useMemo, useCallback } from "react"
import { useTranslation } from "react-i18next"
import { capitalize, cloneDeep, debounce } from "lodash"
import { Switch, FormControlLabel } from "@mui/material"

import { colors } from "@/components/colors"
import TextField from "@/components/form/textField/TextField"
import Selector from "@/components/form/selector/Selector"
import IconButton from "@/components/button/IconButton"
import { GraphSettings, SettingSectionProps } from "@/app/report/common/type"
import { CHART_TYPE, ORDINAL_STRINGS, SECTION_TYPE } from "@/app/report/common/constants"

import DeleteItemDialog from "@/app/report/components/setting/DeleteItemDialog"
import SettingSectionItem from "@/app/report/components/setting/SettingSectionItem"
import AddComponentButton from "@/app/report/components/setting/AddComponentButton"
import MetricSetting from "@/app/report/components/setting/MetricSetting"
import ColorPicker from "@/app/report/components/setting/ColorPicker"
import SortSelector from "@/app/report/components/setting/SortSelector"
import RadioGroup from "@/components/form/radioGroup/RadioGroup"
import Divider from "@/components/divider/Divider"
import { ReportTemplate } from "@/types/report"
import { useTranslateMetricKey } from "@/hooks/metric/useTranslateKeyHook"
import { coerceVerticalChartType, VERTICAL_CHART_TYPES } from "@/app/report/common/sections/graph"

const DEFAULT_Y_AXIS = {
  metric: "",
  chartType: "",
  color: colors.blue[900],
  manualScaleActive: false,
  manualScaleValue: null,
}

export default function GraphSection({
  settings,
  setTempTemplate,
  sectionIndex,
  sectionType = "activity",
}: Omit<SettingSectionProps<GraphSettings[]>, "tempTemplate"> & {
  sectionIndex: number
  sectionType?: "activity" | "player"
}) {
  const { t } = useTranslation()
  const translateKey = useTranslateMetricKey()
  const [activityTempItems, setActivityTempItems] = useState<GraphSettings[]>([])
  const [playerTempItems, setPlayerTempItems] = useState<GraphSettings[]>([])
  const [deleteItemIndex, setDeleteItemIndex] = useState<number | null>(null)
  const [manualScaleValues, setManualScaleValues] = useState<{
    [chartIndex: number]: { [yAxisIndex: number]: string }
  }>({})

  const tempItems = useMemo(
    () => (sectionType === "activity" ? activityTempItems : playerTempItems),
    [sectionType, activityTempItems, playerTempItems],
  )
  const setTempItems = useMemo(
    () => (sectionType === "activity" ? setActivityTempItems : setPlayerTempItems),
    [sectionType, setActivityTempItems, setPlayerTempItems],
  )

  const getSortMenuOptions = (setting: GraphSettings) => {
    const valueChildren = setting.yAxis.map((yAxis) => ({
      key: yAxis.metric,
      label: translateKey(yAxis.metric),
      color: yAxis.color,
      children: [
        {
          key: `${yAxis.metric}-asc`,
          label: t("lowToHigh", { defaultValue: "Low to High" }),
          value: { key: yAxis.metric, order: "asc" as const },
        },
        {
          key: `${yAxis.metric}-desc`,
          label: t("highToLow", { defaultValue: "High to Low" }),
          value: { key: yAxis.metric, order: "desc" as const },
        },
      ],
    }))

    if (sectionType === "activity") {
      return [
        {
          key: "date",
          label: t("date"),
          children: [
            {
              key: "date-asc",
              label: t("lowToHigh", { defaultValue: "Low to High" }),
              value: { key: "date", order: "asc" as const },
            },
            {
              key: "date-desc",
              label: t("highToLow", { defaultValue: "High to Low" }),
              value: { key: "date", order: "desc" as const },
            },
          ],
          is3D: false,
        },
        {
          key: "value",
          label: t("value"),
          children: valueChildren,
          is3D: true,
        },
      ]
    } else {
      return [
        {
          key: "value",
          label: t("value"),
          children: valueChildren,
          is3D: true,
        },
        {
          key: "playerName",
          label: t("playerName"),
          children: [
            {
              key: "playerName-asc",
              label: t("aToZ", { defaultValue: "A - Z" }),
              value: { key: "playerName", order: "asc" as const },
            },
            {
              key: "playerName-desc",
              label: t("zToA", { defaultValue: "Z - A" }),
              value: { key: "playerName", order: "desc" as const },
            },
          ],
          is3D: false,
        },
        {
          key: "jersey",
          label: t("jersey"),
          children: [
            {
              key: "jersey-asc",
              label: t("lowToHigh", { defaultValue: "Low to High" }),
              value: { key: "jersey", order: "asc" as const },
            },
            {
              key: "jersey-desc",
              label: t("highToLow", { defaultValue: "High to Low" }),
              value: { key: "jersey", order: "desc" as const },
            },
          ],
          is3D: false,
        },
        {
          key: "position",
          label: t("position"),
          children: [
            {
              key: "position-asc",
              label: t("fwToGk", { defaultValue: "FW - GK" }),
              value: { key: "position", order: "asc" as const },
            },
            {
              key: "position-desc",
              label: t("gkToFw", { defaultValue: "GK - FW" }),
              value: { key: "position", order: "desc" as const },
            },
          ],
          is3D: false,
        },
      ]
    }
  }

  const debouncedUpdateManualScale = useCallback(
    debounce((chartIndex: number, yAxisIndex: number, value: number | null) => {
      const setter = sectionType === "activity" ? setActivityTempItems : setPlayerTempItems
      setter((prev) => {
        const updated = [...prev]
        updated[chartIndex].yAxis[yAxisIndex] = {
          ...updated[chartIndex].yAxis[yAxisIndex],
          manualScaleValue: value,
        }

        return updated
      })
    }, 200),
    [sectionType],
  )

  useEffect(() => {
    const defaultSortKey = sectionType === "activity" ? "date" : "jersey"
    if (sectionType === "activity") {
      setActivityTempItems(
        cloneDeep(settings).map((item) => ({
          ...item,
          sortSetting: item.sortSetting || { key: defaultSortKey, order: "asc" },
        })),
      )
    } else {
      setPlayerTempItems(
        cloneDeep(settings).map((item) => ({
          ...item,
          sortSetting: item.sortSetting || { key: defaultSortKey, order: "asc" },
        })),
      )
    }
  }, [settings, sectionType])

  useEffect(() => {
    setTempTemplate((prev: ReportTemplate | null) => {
      if (!prev) {
        return prev
      }

      const updatedContent = prev.content.map((section, index) => {
        if (index === sectionIndex) {
          if (sectionType === "activity") {
            return { ...section, settings: activityTempItems }
          } else {
            return { ...section, settings: playerTempItems }
          }
        }

        return section
      })

      return { ...prev, content: updatedContent }
    })
  }, [sectionIndex, sectionType, setTempTemplate, activityTempItems, playerTempItems])

  return (
    <>
      <div className="TrendSettingActivityGraphSectionCont">
        {tempItems.map((setting, index) => {
          return (
            <SettingSectionItem
              key={`graph-item-${index}`}
              onDeleteItem={index > 0 ? () => setDeleteItemIndex(index) : undefined}>
              <div key="display" className="trend-setting-activity-yAxis-row display-row">
                <p className="trend-setting-activity-yAxis-label">{t("display")}</p>
                <div className="trend-setting-display-title-wrapper">
                  <TextField
                    label={t("title")}
                    value={setting.title}
                    onChange={(e) => {
                      const newValue = e.target.value

                      setTempItems((prev) => {
                        const updated = [...prev]
                        updated[index] = { ...updated[index], title: newValue }

                        return updated
                      })
                    }}
                    error={setting.title.length > 35 || setting.title.length < 1}
                    showMessage={setting.title.length > 35 || setting.title.length < 1}
                    message={
                      setting.title.length > 35
                        ? t("rules.max_len", { name: t("title"), max_len: 35 })
                        : t("rules.required")
                    }
                  />
                </div>
                {/* 값 표시·소수점 — #185(661fa0c6)에서 지워졌다가 복구 (2026-10-15 핫픽스) */}
                <div className="trend-setting-display-options-wrapper">
                  <Selector
                    label={t("dataLabels")}
                    options={[
                      { value: "hideValues", label: t("hideValues") },
                      { value: "abovePlots", label: t("abovePlots") },
                      { value: "separateTable", label: t("separateTable") },
                    ]}
                    value={{
                      value: setting.dataLabel || "hideValues",
                      label: t(setting.dataLabel || "hideValues"),
                    }}
                    onChange={(_: unknown, newVal: { value: string; label: string } | null) => {
                      if (!newVal) {
                        return
                      }

                      setTempItems((prev) => {
                        const updated = [...prev]
                        updated[index] = { ...updated[index], dataLabel: newVal.value }

                        return updated
                      })
                    }}
                    placeholder={t("select")}
                  />
                  <Selector
                    label={t("decimalPlaces")}
                    disabled={(setting.dataLabel || "hideValues") === "hideValues"}
                    options={[
                      { value: "0", label: t("none") },
                      { value: "1", label: "0.1" },
                      { value: "2", label: "0.01" },
                    ]}
                    value={(() => {
                      const decimalValue = setting.decimalPlace?.toString() || "1"
                      const options = [
                        { value: "0", label: t("none") },
                        { value: "1", label: "0.1" },
                        { value: "2", label: "0.01" },
                      ]
                      const foundOption = options.find((opt) => opt.value === decimalValue)

                      return foundOption || { value: "1", label: "0.1" }
                    })()}
                    onChange={(_: unknown, newVal: { value: string; label: string } | null) => {
                      if (!newVal) {
                        return
                      }

                      setTempItems((prev) => {
                        const updated = [...prev]
                        updated[index] = {
                          ...updated[index],
                          decimalPlace: parseInt(newVal.value),
                        }

                        return updated
                      })
                    }}
                    placeholder={t("select")}
                  />
                </div>
                <div className="trend-setting-session-details-radio">
                  <p className="label-text">{t("metricCalculationOption")}</p>
                  <RadioGroup
                    direction="row"
                    items={[
                      { label: t("average"), value: "average" },
                      { label: t("total"), value: "total" },
                    ]}
                    value={setting.calcType}
                    onChange={(_, newVal) => {
                      if (!newVal) {
                        return
                      }
                      setTempItems((prev) => {
                        const updated = [...prev]
                        updated[index].calcType = newVal

                        return updated
                      })
                    }}
                  />
                </div>
              </div>
              {/* 차트 방향 — 가로(항목이 X축, 기본) / 세로(항목 세로 나열: 선수 또는 액티비티), 세로면 X축 표시 스위치 (#185) */}
              {
                <>
                  <Divider />
                  <div
                    key="orientation"
                    className="trend-setting-activity-yAxis-row trend-setting-orientation-row">
                    <p className="trend-setting-activity-yAxis-label">
                      {t("orientation", "Orientation")}
                    </p>
                    <RadioGroup
                      direction="row"
                      items={[
                        { label: t("orientationHorizontal", "Horizontal"), value: "horizontal" },
                        { label: t("orientationVertical", "Vertical"), value: "vertical" },
                      ]}
                      value={setting.orientation ?? "horizontal"}
                      onChange={(_, newVal) => {
                        if (!newVal) {
                          return
                        }
                        setTempItems((prev) => {
                          const updated = [...prev]
                          const orientation = newVal as "horizontal" | "vertical"
                          updated[index] = {
                            ...updated[index],
                            orientation,
                            // 세로는 막대·누적 막대만 — 다른 종류는 막대로 바꾼다 (2026-09-30 결정)
                            yAxis:
                              orientation === "vertical"
                                ? updated[index].yAxis.map((axis) => ({
                                    ...axis,
                                    chartType: coerceVerticalChartType(axis.chartType),
                                  }))
                                : updated[index].yAxis,
                          }

                          return updated
                        })
                      }}
                    />
                    {/* Show X axis 스위치는 동작에 문제가 있어 설정 화면에서 숨겼다 (2026-10-15).
                        템플릿의 showAxis 값과 그리는 쪽은 그대로 두었으므로, 고치면 이 블록만 되살리면 된다 */}
                  </div>
                </>
              }
              <Divider />
              <div className="TrendSettingActivityYAxisCont">
                <div key="x-axis" className="trend-setting-activity-yAxis-row first">
                  <p className="trend-setting-activity-yAxis-label">{t("xAxis")}</p>
                  <TextField
                    label={t("name")}
                    value={sectionType === "activity" ? t("date") : t("player")}
                    className="trend-setting-activity-xAxis"
                    slotProps={{
                      input: {
                        readOnly: true,
                      },
                    }}
                  />
                </div>
                {setting.yAxis.map((item, itemIndex) => {
                  return (
                    <div key={itemIndex} className="trend-setting-activity-yAxis-row">
                      <p className="trend-setting-activity-yAxis-label">{`${t(ORDINAL_STRINGS[itemIndex])} ${t("yAxis")}`}</p>
                      <MetricSetting
                        selectedMetric={item.metric}
                        setSelectedMetric={(value) => {
                          setTempItems((prev) => {
                            const updated = [...prev]
                            updated[index].yAxis[itemIndex] = {
                              ...updated[index].yAxis[itemIndex],
                              metric: value,
                            }

                            return updated
                          })
                        }}
                      />
                      <div className="trend-setting-activity-chart-type-wrapper">
                        <Selector
                          label={t("chartType")}
                          options={Object.entries(CHART_TYPE)
                            .map(([, chartType]) => chartType)
                            // 세로 차트는 막대·누적 막대만 고를 수 있다 (2026-09-30 결정)
                            .filter(
                              (chartType) =>
                                (setting.orientation ?? "horizontal") !== "vertical" ||
                                VERTICAL_CHART_TYPES.includes(chartType),
                            )
                            .map((chartType) => ({
                              value: chartType,
                              label:
                                chartType === "stacked" ? t("stackedBar") : capitalize(chartType),
                            }))}
                          value={{
                            value: item.chartType,
                            label:
                              item.chartType === "stacked"
                                ? t("stackedBar")
                                : capitalize(item.chartType),
                          }}
                          renderOption={(props, option) => {
                            const { key, ...optionProps } = props

                            if (!option) {
                              return null
                            }

                            return (
                              <li key={key} {...optionProps}>
                                <p>{option.label}</p>
                              </li>
                            )
                          }}
                          onChange={(_: unknown, newVal: { value: string; label: string }) => {
                            setTempItems((prev) => {
                              const updated = [...prev]
                              updated[index].yAxis[itemIndex] = {
                                ...updated[index].yAxis[itemIndex],
                                chartType: newVal.value,
                              }

                              return updated
                            })
                          }}
                          placeholder={t("select")}
                          error={item.chartType === ""}
                          showMessage={item.chartType === ""}
                          message={t("rules.required")}
                        />
                      </div>
                      <div className="trend-setting-activity-manual-scale-wrapper">
                        <p className="trend-setting-activity-manual-scale-title">
                          {t("manualScale")}
                        </p>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={item.manualScaleActive || false}
                              onChange={(e) => {
                                setTempItems((prev) => {
                                  const updated = [...prev]
                                  updated[index].yAxis[itemIndex] = {
                                    ...updated[index].yAxis[itemIndex],
                                    manualScaleActive: e.target.checked,
                                  }

                                  return updated
                                })
                              }}
                              size="small"
                            />
                          }
                          label=""
                        />
                      </div>
                      <div className="trend-setting-activity-max-value-wrapper">
                        <p className="trend-setting-activity-max-value-title">{t("maxValue")}</p>
                        <TextField
                          value={
                            manualScaleValues[index]?.[itemIndex] !== undefined
                              ? manualScaleValues[index][itemIndex]
                              : item.manualScaleValue || ""
                          }
                          onChange={(e) => {
                            const inputValue = e.target.value

                            if (!/^\d*\.?\d*$/.test(inputValue) && inputValue !== "") {
                              return
                            }

                            setManualScaleValues((prev) => ({
                              ...prev,
                              [index]: {
                                ...prev[index],
                                [itemIndex]: inputValue,
                              },
                            }))

                            const numValue = inputValue === "" ? null : Number(inputValue)
                            debouncedUpdateManualScale(index, itemIndex, numValue)
                          }}
                          disabled={!item.manualScaleActive}
                        />
                      </div>
                      <ColorPicker
                        color={item.color}
                        setColor={(color) => {
                          setTempItems((prev) => {
                            const updated = [...prev]
                            updated[index].yAxis[itemIndex] = {
                              ...updated[index].yAxis[itemIndex],
                              color: color,
                            }

                            return updated
                          })
                        }}
                      />
                      <div className="trend-setting-activity-yAxis-add-wrapper">
                        <IconButton
                          type="default"
                          icon="subtract"
                          size="md"
                          onClick={() => {
                            setTempItems((prev) => {
                              const updated = [...prev]
                              updated[index].yAxis = updated[index].yAxis.filter(
                                (_, i) => i !== itemIndex,
                              )

                              return updated
                            })
                          }}
                          disabled={itemIndex === 0}
                        />
                        <div className="add-button-placeholder">
                          {itemIndex === setting.yAxis.length - 1 && (
                            <IconButton
                              type="default"
                              icon="add"
                              size="md"
                              onClick={() => {
                                setTempItems((prev) => {
                                  const updated = [...prev]
                                  updated[index].yAxis.push({ ...DEFAULT_Y_AXIS })

                                  return updated
                                })
                              }}
                              disabled={itemIndex === 2}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
              <Divider />
              <div key="sort" className="trend-setting-activity-yAxis-row">
                <p className="trend-setting-activity-yAxis-label">{t("sort")}</p>
                <SortSelector
                  value={
                    setting.sortSetting || {
                      key: sectionType === "activity" ? "date" : "jersey",
                      order: "asc",
                    }
                  }
                  onChange={(value) => {
                    setTempItems((prev) => {
                      const updated = [...prev]
                      updated[index] = { ...updated[index], sortSetting: value }

                      return updated
                    })
                  }}
                  options={getSortMenuOptions(setting)}
                />
              </div>
            </SettingSectionItem>
          )
        })}
      </div>

      <DeleteItemDialog
        deleteItemIndex={deleteItemIndex}
        setDeleteItemIndex={setDeleteItemIndex}
        onDeleteAction={(index) => {
          setTempItems((prev) => prev.filter((_, i) => i !== index))
        }}
      />
    </>
  )
}
